import { useEffect, useState } from "react";
import { X, Share, Plus, MoreVertical, Smartphone } from "lucide-react";

const STORAGE_KEY = "pwa-install-dismissed-until";
const DISMISS_DAYS = 7;

const detectPlatform = () => {
  if (typeof window === "undefined") return "unknown";
  const ua = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && window.navigator.maxTouchPoints > 1); // iPadOS 13+
  if (iOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const isDismissed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    return new Date(raw).getTime() > Date.now();
  } catch {
    return false;
  }
};

export default function PWAInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("unknown");
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [showAndroidSteps, setShowAndroidSteps] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = detectPlatform();
    setPlatform(p);

    if (isStandalone() || isDismissed()) return;
    if (p !== "ios" && p !== "android") return;

    // Show after a small delay so it doesn't fight the landing page hero animation
    const timer = setTimeout(() => setVisible(true), 1400);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    const until = new Date(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000).toISOString();
    try { localStorage.setItem(STORAGE_KEY, until); } catch {}
    setVisible(false);
  };

  const installAndroid = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") setVisible(false);
      } catch {}
      setDeferredPrompt(null);
    } else {
      setShowAndroidSteps(true);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 pb-4 sm:pb-0"
      data-testid="pwa-install-banner"
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-gold/40 bg-[#0F172A] text-primary-foreground shadow-2xl relative overflow-hidden"
        style={{ boxShadow: "0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.3)" }}
      >
        {/* Gold glow accent */}
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gold/20 blur-2xl pointer-events-none" />

        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-primary-foreground/10 text-primary-foreground/60 hover:text-primary-foreground transition-colors z-10"
          data-testid="pwa-close-btn"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-8 pb-6 relative">
          {/* Logo */}
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/40 grid place-items-center p-2">
              <img
                src="/images/brand-logo.png"
                alt="Centro de Performance Farmacêutica"
                loading="lazy"
                className="w-full h-full object-contain"
                data-testid="pwa-brand-logo"
              />
            </div>
          </div>

          {/* Headline */}
          <div className="text-center mb-6">
            <div className="overline text-gold mb-2 tracking-[0.22em] text-[10px]">Acesso rápido</div>
            <h3 className="brand-wordmark text-xl font-bold uppercase leading-tight">
              Instale o atalho do<br />
              <span className="text-gold">Centro de Performance</span>
            </h3>
            <p className="mt-3 text-xs text-primary-foreground/70 leading-relaxed">
              Tenha check-ins, chat com o farmacêutico e seu protocolo a um toque na tela inicial do celular.
            </p>
          </div>

          {/* iOS instructions */}
          {platform === "ios" && !showIOSSteps && (
            <button
              type="button"
              onClick={() => setShowIOSSteps(true)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gold text-primary px-4 py-3 text-sm font-bold uppercase tracking-wider hover:bg-gold/90 transition-colors"
              data-testid="pwa-ios-btn"
            >
              <Smartphone className="w-4 h-4" /> Ver como instalar no iPhone
            </button>
          )}

          {platform === "ios" && showIOSSteps && (
            <div className="space-y-3" data-testid="pwa-ios-steps">
              <Step
                num="1"
                icon={<Share className="w-4 h-4 text-gold" />}
                text={<>Toque no botão <strong className="text-gold">Compartilhar</strong> na barra do Safari</>}
              />
              <Step
                num="2"
                icon={<Plus className="w-4 h-4 text-gold" />}
                text={<>Role e escolha <strong className="text-gold">Adicionar à Tela de Início</strong></>}
              />
              <Step
                num="3"
                icon={<Smartphone className="w-4 h-4 text-gold" />}
                text={<>Confirme em <strong className="text-gold">Adicionar</strong> e pronto — o ícone aparece na tela inicial</>}
              />
            </div>
          )}

          {/* Android instructions */}
          {platform === "android" && !showAndroidSteps && (
            <button
              type="button"
              onClick={installAndroid}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gold text-primary px-4 py-3 text-sm font-bold uppercase tracking-wider hover:bg-gold/90 transition-colors"
              data-testid="pwa-android-btn"
            >
              <Smartphone className="w-4 h-4" /> Instalar App
            </button>
          )}

          {platform === "android" && showAndroidSteps && (
            <div className="space-y-3" data-testid="pwa-android-steps">
              <Step
                num="1"
                icon={<MoreVertical className="w-4 h-4 text-gold" />}
                text={<>Toque no <strong className="text-gold">menu (⋮)</strong> no canto superior direito do navegador</>}
              />
              <Step
                num="2"
                icon={<Plus className="w-4 h-4 text-gold" />}
                text={<>Escolha <strong className="text-gold">Instalar app</strong> ou <strong className="text-gold">Adicionar à tela inicial</strong></>}
              />
              <Step
                num="3"
                icon={<Smartphone className="w-4 h-4 text-gold" />}
                text={<>Confirme e o ícone será criado na sua tela inicial</>}
              />
            </div>
          )}

          <button
            type="button"
            onClick={dismiss}
            className="w-full mt-4 text-xs text-primary-foreground/60 hover:text-primary-foreground/90 py-2 transition-colors"
            data-testid="pwa-dismiss-btn"
          >
            Agora não · lembrar em 7 dias
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ num, icon, text }) {
  return (
    <div className="flex items-start gap-3 bg-primary-foreground/5 border border-gold/20 rounded-lg px-3 py-2.5">
      <div className="w-6 h-6 rounded-full bg-gold/15 border border-gold/40 grid place-items-center flex-none text-[10px] font-bold text-gold">
        {num}
      </div>
      <div className="flex-1 flex items-center gap-2 text-xs text-primary-foreground/85 leading-relaxed pt-0.5">
        {icon}
        <span>{text}</span>
      </div>
    </div>
  );
}
